import { create } from "@bufbuild/protobuf";
import { type ClientMessage, RemoveRequestSchema } from "../gen/livesoup/v1/realtime_pb";
import { client } from "../net/codec";
import { $chatOpen } from "../state/chat";
import { slotAction } from "../state/hotbar";
import { $lookMarker, openMarker } from "../state/markers";
import { $hotbar, $unavailable } from "../state/settings";
import { $hotbarIndex, $lookAt, $myAssets, $panel, toast } from "../state/stores";
import { $activeTool, activateTool } from "../state/tools";

/** What the commands need from the room (spec 2o §2.1); RoomController implements it. */
export interface RoomKeys {
  active(): boolean;
  toggle(): void | Promise<void>;
  interact(): void;
  activePlayer(): { setMovementBlocked(b: boolean): void };
}

export type PanelKey = "inventory" | "mail" | "plot";

export interface CommandDeps {
  session: { toggleJoin(): void; ws: { send(m: ClientMessage): void } };
  placing: { active(): boolean; stop(): void; start(code: string): unknown; confirm(): void; rotate(deg: number): void };
  marker: { confirm(): void };
  chat: { open(): void };
  room: RoomKeys;
}

/** Everything a key or a touch button can ask for (spec 2p §3.3). The rules live here, so both obey them. */
export interface Commands {
  back(): void;
  toggleRoom(): void;
  interact(): void;
  openChat(): void;
  openPanel(name: PanelKey): void;
  selectSlot(i: number): void;
  toggleJoin(): void;
  confirm(): void;
  rotate(deg: number): void;
  cancel(): void;
}

const ROOM_PANELS: ReadonlySet<PanelKey> = new Set(["inventory", "mail"]);

export function createCommands(d: CommandDeps): Commands {
  let removeSeq = 1000;
  const busy = () => $chatOpen.get() || !!$panel.get();
  const releasePointer = () => document.exitPointerLock?.();
  return {
    back() {
      if ($chatOpen.get()) return; // the chat input closes itself
      if ($activeTool.get()) { $activeTool.set(null); return; }
      if (d.placing.active()) { d.placing.stop(); return; }
      if ($panel.get()) { $panel.set(null); return; }
      $panel.set("menu");
      releasePointer();
    },
    toggleRoom() {
      if (busy()) return;
      $activeTool.set(null);
      d.placing.stop();
      void d.room.toggle();
    },
    interact() {
      if (busy()) return;
      if (d.room.active()) { d.room.interact(); return; }
      const mk = $lookMarker.get();
      if (mk) { openMarker(mk); return; } // a pin wins over removing (spec 2h §2.4)
      const look = $lookAt.get();
      if (look?.removable) d.session.ws.send(client({ case: "remove", value: create(RemoveRequestSchema, { requestSeq: ++removeSeq, entityId: look.entityId }) }));
    },
    openChat() {
      if (busy()) return;
      $activeTool.set(null);
      d.placing.stop();
      d.chat.open();
    },
    openPanel(name) {
      if (busy()) return;
      if (d.room.active() && !ROOM_PANELS.has(name)) return; // in the room only the inventory and mail (spec 2o §2.1)
      $panel.set(name);
      releasePointer();
    },
    selectSlot(i) {
      if (busy() || d.room.active()) return;
      $hotbarIndex.set(i);
      const act = slotAction($hotbar.get()[i] ?? null, $unavailable.get(), $myAssets.get());
      if (act.kind !== "tool") $activeTool.set(null);
      if (act.kind === "place") { void d.placing.start(act.code); return; }
      d.placing.stop();
      if (act.kind === "tool") activateTool(act.id);
      if (act.kind === "unavailable") toast("此資產不可用");
    },
    toggleJoin() {
      if (busy() || d.room.active()) return;
      d.session.toggleJoin();
    },
    confirm() {
      if (busy()) return;
      if (d.placing.active()) d.placing.confirm();
      else if ($activeTool.get() === "marker") d.marker.confirm();
    },
    rotate(deg) {
      if (!busy() && d.placing.active()) d.placing.rotate(deg);
    },
    cancel() {
      if ($activeTool.get()) $activeTool.set(null);
      else d.placing.stop();
    },
  };
}
